import { createReducer, Action, on} from '@ngrx/store';
import { createEntityAdapter, Dictionary, EntityAdapter } from '@ngrx/entity';
import * as fromActions from './table.actions';

import { Pagination, UserRole } from '@app/core/models';
import { AssignRoleTableFilters, INITIAL_ASSIGN_ROLES_FILTERS } from '@app/shared/filters/models/assign-role-table';
import { TableState, flattenFilters } from '@app/shared/filters/filters-model';

export const feature = 'role-assign-table';

export const adapter: EntityAdapter<UserRole> =
 createEntityAdapter<UserRole>({
  selectId: (e) => e.login
 });

export interface State {
  table: TableState<UserRole, AssignRoleTableFilters>;

}

export const initialState: State = {
  table: {
    data: adapter.getInitialState(),
    pagination: {
      ...Pagination,
      pageIndex: 0,
      pageSize: 10,
    },
    sort: {
      active: 'name',
      direction: 'asc'
    },
    defaultSort: {
      active: 'name',
      direction: 'asc'
    },
    filters: INITIAL_ASSIGN_ROLES_FILTERS,
    defaultFilters: INITIAL_ASSIGN_ROLES_FILTERS,
    isLoading: false,
    resetIndex: false,
  },

};

const tableReducer = createReducer(
  initialState,
  on(fromActions.getTableRequest, (state) => ({
    ...state,
    table: {
      ...state.table,
      isLoading: true
    }
  })),
  on(fromActions.getTableSuccess, fromActions.getTableFailure, (state) => ({
    ...state,
    table: {
      ...state.table,
      isLoading: false
    }
  })),
  on(fromActions.getTableSuccess, (state, {data, pagination}) => ({
    ...state,
    table: {
      ...state.table,
      data: adapter.setAll(data, state.table.data),
      pagination,
      resetIndex: false,
    }
  })),
  on(fromActions.changeTableFilters, (state, {filters}) => ({
    ...state,
    table: {
      ...state.table,
      filters,
      resetIndex: true
    }
  })),
  on(fromActions.changeTableSort, (state, {active, direction}) => ({
    ...state,
    table: {
      ...state.table,
      sort: {
        active,
        direction
      }
    }
  })),
  on(fromActions.changeTablePagination, (state, {pageIndex, pageSize}) => ({
    ...state,
    table: {
      ...state.table,
      pagination: {
        ...state.table.pagination,
        pageIndex,
        pageSize
      }
    }
  })),
  on(fromActions.deleteUserRoleSuccess, (state) => ({
    ...state,
    table: {
      ...state.table,
      resetIndex: true
    }
  })),
  on(fromActions.clearAll, (state) => ({
    ...initialState
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return tableReducer(state, action);
}

// NEW WAY TABLE
export const getTableState = (state: State) => state.table;

export const getPagination = (table: TableState<UserRole, AssignRoleTableFilters>) => table.pagination;
export const getSort = (table: TableState<UserRole, AssignRoleTableFilters>) => table.sort;
export const getDefaultSort = (table: TableState<UserRole, AssignRoleTableFilters>) => table.defaultSort;
export const getFilters = (table: TableState<UserRole, AssignRoleTableFilters>) => table.filters;
export const getDefaultFilters = (table: TableState<UserRole, AssignRoleTableFilters>) => table.defaultFilters;
export const getIsLoading = (table: TableState<UserRole, AssignRoleTableFilters>) => table.isLoading;

export const getRequestData = ({pagination, sort, filters, resetIndex}: TableState<UserRole, AssignRoleTableFilters>) => ({
  pageIndex: pagination.pageIndex,
  pageSize: pagination.pageSize,
  sort,
  filters: flattenFilters(filters),
  resetIndex
});

export const getTableData = (table: TableState<UserRole, AssignRoleTableFilters>) => adapter.getSelectors().selectAll(table.data);

// Pagination fields
export const getPageSize = (pagination: Pagination) => pagination.pageSize;
export const getPageIndex = (pagination: Pagination) => pagination.pageIndex;
export const getTotalCount = (pagination: Pagination) => pagination.totalCount;

// Filters fields
export const getSearch = (filters: AssignRoleTableFilters) => filters.search.value;

export const getEntitiesByIds = (entities: Dictionary<UserRole>, ids: string[]): UserRole[] => ids.map(id => entities[id]);
